import clsx from "clsx";
import deburr from "lodash/deburr";
import React from "react";
import Highlighter from "react-highlight-words";
import styles from "web/react/components/auto-complete/auto-complete.module.css";
import { AutoCompleteContext } from "./auto-complete.context";

function AutoComplete(): React.ReactElement {
    const {
        query,
        activeSuggestionIndex,
        hasAnySuggestions,
        querySuggestions,
        profileSuggestions,
        makeSuggestionActive,
        onSuggestionClick,
        isVisible,
    } = React.useContext(AutoCompleteContext);

    const [touchMoved, setTouchMoved] = React.useState<boolean>(false);

    /**
     * Used by touch device
     */
    function onTouchMove(): void {
        setTouchMoved(true);
    }

    /**
     * Used by touch device
     */
    function onTouchStart(): void {
        setTouchMoved(false);
    }

    /**
     * Used by touch device, calls onSuggestionClick only when it's not scrolling
     */
    function onTouchEnd(event): void {
        if (!touchMoved) {
            onSuggestionClick(event);
        }
    }

    function onMouseEnter(event): void {
        makeSuggestionActive(parseInt(event.target.dataset.suggestionGlobalIndex, 0));
    }

    function onMouseLeave(event): void {
        makeSuggestionActive(parseInt(event.target.dataset.suggestionGlobalIndex, 0));
    }

    return (
        <ul
            className={clsx("auto-complete", {
                "auto-complete--empty": !hasAnySuggestions,
                hidden: !isVisible,
            })}
        >
            {querySuggestions.map((suggestion, index) => (
                <li
                    key={`query_${index}_${suggestion.query}`}
                    className={clsx("auto-complete__suggestion auto-complete__suggestion--query", {
                        "auto-complete__suggestion--active": activeSuggestionIndex === index,
                    })}
                    data-suggestion-global-index={index}
                    data-suggestion-type="query"
                    data-suggestion-index={index}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onMouseDown={onSuggestionClick}
                >
                    <Highlighter
                        highlightClassName={styles.highlighted}
                        searchWords={[query]}
                        textToHighlight={suggestion.query}
                        autoEscape={true}
                        sanitize={deburr}
                    />
                </li>
            ))}
            {profileSuggestions.map((suggestion, index) => (
                <li
                    key={`profile_${index}_${suggestion.profile_name}`}
                    className={clsx(
                        "auto-complete__suggestion auto-complete__suggestion--profile",
                        {
                            "auto-complete__suggestion--active":
                                activeSuggestionIndex === querySuggestions.length + index,
                        }
                    )}
                    data-suggestion-global-index={querySuggestions.length + index}
                    data-suggestion-type="profile"
                    data-suggestion-index={index}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onMouseDown={onSuggestionClick}
                >
                    <div className="media-block">
                        <div className="media-image">
                            <img
                                className="auto-complete__profile-avatar"
                                src={suggestion.profile_avatar}
                            />
                        </div>
                        <div className="media-content">
                            <div className="auto-complete__profile-name">
                                {suggestion.profile_name}
                            </div>
                            <div className="auto-complete__profile-type">
                                {suggestion.profile_type}
                            </div>
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    );
}

export default AutoComplete;
